import { useState } from 'react'
import { X } from 'lucide-react'
import { toast } from 'sonner'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { DeleteIcon } from '@/components/icons'
import { useDeleteTestPlan } from '@/hooks'
import type { TestPlan } from '@/types'

interface DeleteTestPlanDialogProps {
  projectId: string
  testPlan: TestPlan
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function DeleteTestPlanDialog({ projectId, testPlan, open, onOpenChange }: DeleteTestPlanDialogProps) {
  const deleteTestPlan = useDeleteTestPlan(projectId)
  const [confirmInput, setConfirmInput] = useState('')

  const isMatch = confirmInput.trim() === testPlan.title

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) setConfirmInput('')
    onOpenChange(nextOpen)
  }

  const handleDelete = async () => {
    if (!isMatch) return
    try {
      await deleteTestPlan.mutateAsync(testPlan.id)
      toast.success(`${testPlan.title} deleted`)
      handleOpenChange(false)
    } catch {
      toast.error('Failed to delete test plan')
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-[640px] rounded-2xl p-8 gap-6 border-none shadow-lg" hideCloseButton>
        <DialogHeader className="flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-2xl font-bold leading-[1.3] tracking-[0.015em] text-primary">Delete Test Plan?</DialogTitle>
          <DialogClose className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none">
            <X className="h-6 w-6" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>
        <DialogDescription className="text-sm font-normal leading-[1.6] tracking-[0.015em] text-primary">
          Are you sure you want to delete the test plan "<span className="font-bold">{testPlan.title}</span>"?
          This action cannot be undone. All linked test suites and their test cases will also be permanently deleted.
        </DialogDescription>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="confirm-plan-title" className="text-sm font-medium text-primary">
            Type the test plan name to confirm
          </Label>
          <Input
            id="confirm-plan-title"
            value={confirmInput}
            onChange={(e) => setConfirmInput(e.target.value)}
            placeholder={testPlan.title}
            data-testid={`confirm-delete-plan-input-${testPlan.testPlanIdentifier}`}
          />
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button
              variant="ghost"
              disabled={deleteTestPlan.isPending}
              className="h-10 px-4 font-bold"
              data-testid={`cancel-delete-test-plan-${testPlan.testPlanIdentifier}`}
            >
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={!isMatch || deleteTestPlan.isPending}
            className="h-10 min-w-10 rounded-lg py-2.5 px-4 gap-2.5 font-bold [&_svg]:size-6"
            data-testid={`confirm-delete-test-plan-${testPlan.testPlanIdentifier}`}
          >
            <DeleteIcon className="h-6 w-6" />
            {deleteTestPlan.isPending ? 'Deleting...' : 'Delete Test Plan'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
